import { inject } from '@angular/core';
import { patchState, signalStoreFeature, type, withMethods } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { catchError, exhaustMap, forkJoin, map, of, pipe, switchMap, tap } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { IPayrollState } from './payroll.state';
import { withPayrollStateMethods } from './payroll.methods.state';
import { PayrollService } from '../../services/payroll/payroll.service';
import { NotificationService } from '../../services/notification/notification.service';
import {
  CreateAllowanceTypePayload,
  CreateContributionSettingPayload,
  CreateDeductionTypePayload,
  CreatePayrollPeriodPayload,
  CreatePayrollSchedulePayload,
  EditContributionSettingPayload,
  EditPayrollPeriodPayload,
  EditPayrollSchedulePayload,
} from '../../models/payroll/payroll-config.model';
import { CreatePayrollRunPayload } from '../../models/payroll/payroll-run.model';
import {
  CreateEmployeeDeductionPayload,
  EditEmployeeDeductionPayload,
} from '../../models/payroll/employee-deduction.model';

export const withPayrollApiMethods = signalStoreFeature(
  { state: type<IPayrollState>() },
  withPayrollStateMethods,
  withMethods((store) => {
    const payrollService = inject(PayrollService);
    const notificationService = inject(NotificationService);

    const handleError = (err: HttpErrorResponse) => {
      const msg = err.error?.message ?? err.message;
      store.setError(msg);
      notificationService.notifyError('Error', msg);
    };

    return {
      loadRuns: rxMethod<number | undefined>(
        pipe(
          tap(() => patchState(store, { loading: true, error: null })),
          exhaustMap((payrollPeriodId) =>
            payrollService.getPayrollRuns(payrollPeriodId).pipe(
              tapResponse({
                next: (res) => store.setRuns(res.payrollRuns),
                error: handleError,
              })
            )
          )
        )
      ),

      loadRunByGuid: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { loading: true, error: null })),
          exhaustMap((guid) =>
            payrollService.getPayrollRunByGuid(guid).pipe(
              tapResponse({
                next: (res) => store.setSelectedRun(res.payrollRun),
                error: handleError,
              })
            )
          )
        )
      ),

      loadResults: rxMethod<number>(
        pipe(
          tap(() => patchState(store, { loading: true, error: null })),
          exhaustMap((runId) =>
            payrollService.getPayrollResults(runId).pipe(
              tapResponse({
                next: (res) => store.setResults(res.payrollResults),
                error: handleError,
              })
            )
          )
        )
      ),

      loadPayslips: rxMethod<number>(
        pipe(
          tap(() => patchState(store, { loading: true, error: null })),
          exhaustMap((runId) =>
            payrollService.getPayslips(runId).pipe(
              tapResponse({
                next: (res) => store.setPayslips(res.payslips),
                error: handleError,
              })
            )
          )
        )
      ),

      loadPeriods: rxMethod<number | undefined>(
        pipe(
          tap(() => patchState(store, { loading: true, error: null })),
          exhaustMap((scheduleId) =>
            payrollService.getPeriods(scheduleId).pipe(
              tapResponse({
                next: (res) => store.setPeriods(res.payrollPeriods),
                error: handleError,
              })
            )
          )
        )
      ),

      loadSchedules: rxMethod<void>(
        pipe(
          tap(() => patchState(store, { loading: true, error: null })),
          exhaustMap(() =>
            payrollService.getSchedules().pipe(
              tapResponse({
                next: (res) => store.setSchedules(res.payrollSchedules),
                error: handleError,
              })
            )
          )
        )
      ),

      loadConfigData: rxMethod<void>(
        pipe(
          tap(() => patchState(store, { loading: true, error: null })),
          exhaustMap(() =>
            forkJoin({
              schedules: payrollService.getSchedules(),
              periods: payrollService.getPeriods(),
              contributions: payrollService.getContributionSettings(),
              tax: payrollService.getTaxBrackets(),
              allowances: payrollService.getAllowanceTypes(),
              deductions: payrollService.getDeductionTypes(),
            }).pipe(
              tapResponse({
                next: (res) => {
                  store.setSchedules(res.schedules.payrollSchedules);
                  store.setPeriods(res.periods.payrollPeriods);
                  store.setContributionSettings(res.contributions.contributionSettings);
                  store.setTaxBrackets(res.tax.taxBrackets);
                  store.setAllowanceTypes(res.allowances.allowanceTypes);
                  store.setDeductionTypes(res.deductions.deductionTypes);
                },
                error: handleError,
              })
            )
          )
        )
      ),

      loadEmployeeDeductions: rxMethod<number | undefined>(
        pipe(
          tap(() => patchState(store, { loading: true, error: null })),
          exhaustMap((employeeId) =>
            payrollService.getEmployeeDeductions(employeeId).pipe(
              tapResponse({
                next: (res) => store.setEmployeeDeductions(res.employeeDeductions),
                error: handleError,
              })
            )
          )
        )
      ),

      loadDtrStatusByPeriod: rxMethod<number>(
        pipe(
          exhaustMap((periodId) =>
            payrollService.getDtrStatusByPeriod(periodId).pipe(
              tapResponse({
                next: (res) => store.setDtrStatuses(res.dtrStatuses),
                error: handleError,
              })
            )
          )
        )
      ),

      createRun: rxMethod<{ payload: CreatePayrollRunPayload; onSuccess?: (run: import('../../models/payroll/payroll-run.model').PayrollRunApi) => void }>(
        pipe(
          tap(() => patchState(store, { loading: true, error: null })),
          exhaustMap(({ payload, onSuccess }) =>
            payrollService.createPayrollRun(payload).pipe(
              tapResponse({
                next: (res) => {
                  store.addRun(res.payrollRun);
                  notificationService.notify('success', 'Created', 'Payroll run created.');
                  onSuccess?.(res.payrollRun);
                },
                error: handleError,
              })
            )
          )
        )
      ),

      processRun: rxMethod<{ guid: string; onSuccess?: () => void }>(
        pipe(
          exhaustMap(({ guid, onSuccess }) =>
            payrollService.processPayrollRun(guid).pipe(
              tapResponse({
                next: (res) => {
                  store.updateRun(res.payrollRun);
                  onSuccess?.();
                },
                error: handleError,
              })
            )
          )
        )
      ),

      unlockRun: rxMethod<{ guid: string; onSuccess?: () => void }>(
        pipe(
          tap(() => patchState(store, { loading: true, error: null })),
          exhaustMap(({ guid, onSuccess }) =>
            payrollService.unlockPayrollRun(guid).pipe(
              tapResponse({
                next: (res) => {
                  store.updateRun(res.payrollRun);
                  store.setResults([]);
                  store.setPayslips([]);
                  notificationService.notify(
                    'success',
                    'Unlocked',
                    'Payroll run is back to Draft. Deduction balances were restored. You can process again.',
                  );
                  onSuccess?.();
                },
                error: handleError,
              })
            )
          )
        )
      ),

      deleteRun: rxMethod<{ guid: string; onSuccess?: () => void }>(
        pipe(
          tap(() => patchState(store, { loading: true, error: null })),
          exhaustMap(({ guid, onSuccess }) =>
            payrollService.deletePayrollRun(guid).pipe(
              tapResponse({
                next: () => {
                  patchState(store, { loading: false });
                  notificationService.notify(
                    'success',
                    'Draft deleted',
                    'The payroll run was deleted. You can create a new one.',
                  );
                  onSuccess?.();
                },
                error: handleError,
              })
            )
          )
        )
      ),

      createSchedule: rxMethod<{ payload: CreatePayrollSchedulePayload; onSuccess?: () => void }>(
        pipe(
          exhaustMap(({ payload, onSuccess }) =>
            payrollService.createSchedule(payload).pipe(
              tapResponse({
                next: (res) => {
                  store.addSchedule(res.payrollSchedule);
                  notificationService.notify('success', 'Created', 'Schedule added.');
                  onSuccess?.();
                },
                error: handleError,
              })
            )
          )
        )
      ),

      editSchedule: rxMethod<{ payload: EditPayrollSchedulePayload; onSuccess?: () => void }>(
        pipe(
          exhaustMap(({ payload, onSuccess }) =>
            payrollService.editSchedule(payload).pipe(
              tapResponse({
                next: (res) => {
                  store.updateSchedule(res.payrollSchedule);
                  notificationService.notify('success', 'Updated', 'Schedule updated.');
                  onSuccess?.();
                },
                error: handleError,
              })
            )
          )
        )
      ),

      createPeriod: rxMethod<{ payload: CreatePayrollPeriodPayload; onSuccess?: () => void }>(
        pipe(
          exhaustMap(({ payload, onSuccess }) =>
            payrollService.createPeriod(payload).pipe(
              tapResponse({
                next: (res) => {
                  store.addPeriod(res.payrollPeriod);
                  notificationService.notify('success', 'Generated', 'Pay period created.');
                  onSuccess?.();
                },
                error: handleError,
              })
            )
          )
        )
      ),

      editPeriod: rxMethod<{ payload: EditPayrollPeriodPayload; onSuccess?: () => void }>(
        pipe(
          exhaustMap(({ payload, onSuccess }) =>
            payrollService.editPeriod(payload).pipe(
              tapResponse({
                next: (res) => {
                  store.updatePeriod(res.payrollPeriod);
                  onSuccess?.();
                },
                error: handleError,
              })
            )
          )
        )
      ),

      editContributionSetting: rxMethod<{ payload: EditContributionSettingPayload; onSuccess?: () => void }>(
        pipe(
          exhaustMap(({ payload, onSuccess }) =>
            payrollService.editContributionSetting(payload).pipe(
              tapResponse({
                next: (res) => {
                  store.updateContributionSetting(res.contributionSetting);
                  notificationService.notify('success', 'Updated', 'Contribution rates updated.');
                  onSuccess?.();
                },
                error: handleError,
              })
            )
          )
        )
      ),

      createAllowanceType: rxMethod<{ payload: CreateAllowanceTypePayload; onSuccess?: () => void }>(
        pipe(
          exhaustMap(({ payload, onSuccess }) =>
            payrollService.createAllowanceType(payload).pipe(
              tapResponse({
                next: (res) => {
                  store.addAllowanceType(res.allowanceType);
                  onSuccess?.();
                },
                error: handleError,
              })
            )
          )
        )
      ),

      deleteAllowanceType: rxMethod<{ guid: string; onSuccess?: () => void }>(
        pipe(
          exhaustMap(({ guid, onSuccess }) =>
            payrollService.deleteAllowanceType(guid).pipe(
              tapResponse({
                next: () => {
                  store.removeAllowanceType(guid);
                  onSuccess?.();
                },
                error: handleError,
              })
            )
          )
        )
      ),

      createDeductionType: rxMethod<{ payload: CreateDeductionTypePayload; onSuccess?: () => void }>(
        pipe(
          exhaustMap(({ payload, onSuccess }) =>
            payrollService.createDeductionType(payload).pipe(
              tapResponse({
                next: (res) => {
                  store.addDeductionType(res.deductionType);
                  onSuccess?.();
                },
                error: handleError,
              })
            )
          )
        )
      ),

      deleteDeductionType: rxMethod<{ guid: string; onSuccess?: () => void }>(
        pipe(
          exhaustMap(({ guid, onSuccess }) =>
            payrollService.deleteDeductionType(guid).pipe(
              tapResponse({
                next: () => {
                  store.removeDeductionType(guid);
                  onSuccess?.();
                },
                error: handleError,
              })
            )
          )
        )
      ),

      createEmployeeDeduction: rxMethod<{
        payloads: CreateEmployeeDeductionPayload[];
        onSuccess?: (saved: number) => void;
        onError?: () => void;
      }>(
        pipe(
          exhaustMap(({ payloads, onSuccess, onError }) =>
            forkJoin(
              payloads.map(payload =>
                payrollService.createEmployeeDeduction(payload).pipe(
                  map(res => ({ ok: true as const, deduction: res.employeeDeduction, message: '' })),
                  catchError((err: HttpErrorResponse) => of({
                    ok: false as const,
                    deduction: null,
                    message: (err.error?.message as string | undefined) ?? 'The deduction was not added.',
                  })),
                ),
              ),
            ).pipe(
              tapResponse({
                next: (results) => {
                  const saved = results.filter(result => result.ok && result.deduction);
                  for (const result of saved) {
                    if (result.deduction) store.addEmployeeDeduction(result.deduction);
                  }
                  if (saved.length === 0) {
                    const message = results.find(result => result.message)?.message ?? 'The deduction was not added.';
                    notificationService.notifyError('Error', message);
                    onError?.();
                    return;
                  }
                  onSuccess?.(saved.length);
                },
                error: (err: HttpErrorResponse) => {
                  handleError(err);
                  onError?.();
                },
              }),
            ),
          ),
        ),
      ),

      editEmployeeDeduction: rxMethod<{ payload: EditEmployeeDeductionPayload; onSuccess?: () => void; onError?: () => void }>(
        pipe(
          exhaustMap(({ payload, onSuccess, onError }) =>
            payrollService.editEmployeeDeduction(payload).pipe(
              tapResponse({
                next: (res) => {
                  store.updateEmployeeDeduction(res.employeeDeduction);
                  onSuccess?.();
                },
                error: (err: HttpErrorResponse) => {
                  handleError(err);
                  onError?.();
                },
              })
            )
          )
        )
      ),
    };
  })
);
